import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useCart, type CartProduct } from '@/context/CartContext';
import { useToast } from '@/components/feature/Toast';
import { supabase } from '@/lib/supabase';
import ProductCard from '@/components/feature/ProductCard';
import CartSidebar from '@/components/feature/CartSidebar';
import SalesModal from '@/components/feature/SalesModal';

interface Producto {
  id: string;
  nombre: string;
  descripcion: string;
  tipo_producto: string;
  laboratorio: string;
  principio_activo: string;
  precio: number;
  stock: number;
  stock_minimo: number;
  imagen: string;
  created_at: string;
  updated_at: string;
}

type SortOption = 'nombre-asc' | 'nombre-desc' | 'precio-asc' | 'precio-desc' | 'stock-asc' | 'stock-desc';

export default function DashboardPage() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { addToast } = useToast();

  const [productos, setProductos] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setCategoriaFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortOption>('nombre-asc');

  const [cartOpen, setCartOpen] = useState(false);
  const [salesOpen, setSalesOpen] = useState(false);

  const [todayStats, setTodayStats] = useState({ ventas: 0, ingresos: 0 });

  useEffect(() => {
    loadProductos();
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      const today = new Date();
      const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0).toISOString();
      const todayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59).toISOString();

      const { data: ventasHoy, error: errHoy } = await supabase
        .from('ventas')
        .select('total')
        .gte('fecha', todayStart)
        .lte('fecha', todayEnd);

      if (!errHoy && ventasHoy) {
        const ingresos = ventasHoy.reduce((s: number, v: { total: number }) => s + Number(v.total), 0);
        setTodayStats({ ventas: ventasHoy.length, ingresos });
      }
    } catch {
      // Stats are optional, don't break the page
    }
  };

  const loadProductos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('productos')
        .select('*')
        .order('nombre', { ascending: true });

      if (error) throw error;

      setProductos((data || []) as Producto[]);
    } catch {
      setProductos([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = [...productos];

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (p) =>
          p.nombre.toLowerCase().includes(q) ||
          p.tipo_producto?.toLowerCase().includes(q) ||
          p.laboratorio?.toLowerCase().includes(q) ||
          p.principio_activo?.toLowerCase().includes(q)
      );
    }

    if (tipoFilter) {
      result = result.filter((p) => p.tipo_producto === tipoFilter);
    }

    switch (sortBy) {
      case 'nombre-asc':
        result.sort((a, b) => a.nombre.localeCompare(b.nombre));
        break;
      case 'nombre-desc':
        result.sort((a, b) => b.nombre.localeCompare(a.nombre));
        break;
      case 'precio-asc':
        result.sort((a, b) => a.precio - b.precio);
        break;
      case 'precio-desc':
        result.sort((a, b) => b.precio - a.precio);
        break;
      case 'stock-asc':
        result.sort((a, b) => a.stock - b.stock);
        break;
      case 'stock-desc':
        result.sort((a, b) => b.stock - a.stock);
        break;
    }

    return result;
  }, [productos, search, tipoFilter, sortBy]);

  const handleAddToCart = (product: CartProduct) => {
    addToCart(product);
    addToast(`${product.nombre} agregado al carrito`, 'info');
  };

  const handleCheckout = () => {
    setCartOpen(false);
    setSalesOpen(true);
  };

  const tiposList = useMemo(
    () => [...new Set(productos.map((p) => p.tipo_producto).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [productos]
  );

  const lowStockCount = productos.filter((p) => p.stock > 0 && p.stock <= (p.stock_minimo || 5)).length;
  const outOfStockCount = productos.filter((p) => p.stock <= 0).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-800">
            Bienvenido{user?.nombre ? `, ${user.nombre.split(' ')[0]}` : ''}
          </h1>
          <p className="text-foreground-500 text-sm mt-1">Punto de Venta</p>
        </div>

        <div className="flex items-center gap-3">
          {(lowStockCount > 0 || outOfStockCount > 0) && (
            <div className="flex items-center gap-2">
              {lowStockCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium rounded-full">
                  <i className="ri-alert-line"></i>
                  {lowStockCount} stock bajo
                </span>
              )}
              {outOfStockCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-accent-50 border border-accent-200 text-accent-700 text-xs font-medium rounded-full">
                  <i className="ri-close-circle-line"></i>
                  {outOfStockCount} agotado{outOfStockCount > 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}

          <button
            onClick={() => setCartOpen(true)}
            className="relative inline-flex items-center gap-2 px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-2-line"></i>
            <span className="hidden sm:inline">Carrito</span>
            <CartBadge />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickStatCard
          icon="ri-shopping-bag-line"
          iconBg="bg-primary-100"
          iconColor="text-primary-600"
          label="Ventas de Hoy"
          value={todayStats.ventas.toString()}
        />
        <QuickStatCard
          icon="ri-money-dollar-circle-line"
          iconBg="bg-accent-100"
          iconColor="text-accent-600"
          label="Ingresos de Hoy"
          value={`Bs. ${todayStats.ingresos.toFixed(2)}`}
        />
        <QuickStatCard
          icon="ri-alert-line"
          iconBg="bg-amber-100"
          iconColor="text-amber-600"
          label="Stock Bajo"
          value={lowStockCount.toString()}
          sub="Productos en o bajo su stock mínimo"
        />
        <QuickStatCard
          icon="ri-close-circle-line"
          iconBg="bg-accent-100"
          iconColor="text-accent-600"
          label="Agotados"
          value={outOfStockCount.toString()}
          sub="Productos sin stock"
        />
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre, tipo, laboratorio o principio activo..."
              className="w-full pl-10 pr-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-800 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
            />
          </div>

          <select
            value={tipoFilter}
            onChange={(e) => setCategoriaFilter(e.target.value)}
            className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="">Todos los tipos</option>
            {tiposList.map((tipo) => (
              <option key={tipo} value={tipo}>{tipo}</option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-foreground-700 text-sm focus:outline-none focus:border-primary-400 cursor-pointer"
          >
            <option value="nombre-asc">Nombre A-Z</option>
            <option value="nombre-desc">Nombre Z-A</option>
            <option value="precio-asc">Precio menor a mayor</option>
            <option value="precio-desc">Precio mayor a menor</option>
            <option value="stock-asc">Stock menor a mayor</option>
            <option value="stock-desc">Stock mayor a menor</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
            <p className="text-foreground-500 text-sm">Cargando productos...</p>
          </div>
        </div>
      ) : filteredAndSorted.length === 0 ? (
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 bg-background-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-search-line text-3xl text-foreground-300"></i>
          </div>
          <h3 className="text-lg font-semibold text-foreground-600 mb-1">Sin resultados</h3>
          <p className="text-foreground-400 text-sm">
            No se encontraron productos con los filtros aplicados
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2">
          {filteredAndSorted.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAdd={handleAddToCart}
            />
          ))}
        </div>
      )}

      <CartSidebar
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        onCheckout={handleCheckout}
      />

      <SalesModal
        open={salesOpen}
        onClose={() => setSalesOpen(false)}
        onSaleComplete={() => {
          loadProductos();
          loadDashboardStats();
        }}
      />
    </div>
  );
}

function QuickStatCard({ icon, iconBg, iconColor, label, value, sub }: {
  icon: string;
  iconBg: string;
  iconColor: string;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-background-200 p-4 hover:border-background-300 transition-all duration-200">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${iconBg} ${iconColor}`}>
          <i className={`${icon} text-lg`}></i>
        </div>
        <div className="min-w-0">
          <p className="text-xs text-foreground-500 truncate">{label}</p>
          <p className="text-lg font-bold text-foreground-800">{value}</p>
          {sub && <p className="text-[10px] text-foreground-400 mt-0.5">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function CartBadge() {
  const { totalItems } = useCart();
  if (totalItems === 0) return null;
  return (
    <span className="absolute -top-2 -right-2 bg-accent-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">
      {totalItems > 9 ? '9+' : totalItems}
    </span>
  );
}